// src/services/email.js
// Now routes through the backend's /api/send-email (Brevo), not EmailJS
// directly — neither Resend nor Brevo has a public-key client mode like
// EmailJS did, so the send genuinely has to happen server-side. Same two
// logical templates as before (welcome/OTP, and notification/security),
// same call shapes for existing callers (otpService.js, AuthContext.js) —
// only the transport changed.
import { auth } from '../config/firebase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

async function sendViaBackend(type, payload) {
  try {
    // Force a fresh token (true), not a possibly-stale cached one — this is
    // often called seconds after account creation (the signup OTP fires
    // immediately in onAuthStateChanged), the highest-risk moment for a
    // just-issued token not being fully ready yet.
    const token = await auth.currentUser?.getIdToken(true);
    if (!token) throw new Error('Not signed in');
    const res = await fetch(`${BACKEND_URL}/api/send-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ type, payload }),
    });
    if (!res.ok) {
      // Previously unchecked — a failed send looked identical to a
      // successful one from here, so failures were invisible even in the
      // browser console.
      throw new Error(`Backend responded ${res.status}: ${await res.text().catch(() => '')}`);
    }
  } catch (err) {
    console.error(`Backend email send (${type}) failed:`, err);
  }
}

// Fire-and-forget by design — a failed email must never block signup or
// status-change UI flows. Callers should not await these for UI purposes.
export async function sendWelcomeEmail(profile, code = '', purposeText = '') {
  await sendViaBackend('welcome-otp', {
    to_email: profile?.email || '',
    to_name: profile?.displayName || 'Student',
    code,
    purpose_text: purposeText,
  });
}

export async function sendOtpEmail(profile, code, purposeText) {
  await sendViaBackend('welcome-otp', {
    to_email: profile?.email || '',
    to_name: profile?.displayName || 'Student',
    code,
    purpose_text: purposeText,
  });
}

export async function sendApplicationNotificationEmail(profile, opportunity) {
  await sendViaBackend('notification', {
    to_email: profile?.email || '',
    to_name: profile?.displayName || 'Student',
    opportunity_title: opportunity?.title || '',
    opportunity_organization: opportunity?.organization || '',
    opportunity_deadline: opportunity?.deadline || 'N/A',
    opportunity_link: opportunity?.link || '',
  });
}

export async function sendSecurityNoticeEmail(profile, eventText, reportUrl) {
  await sendViaBackend('notification', {
    to_email: profile?.email || '',
    to_name: profile?.displayName || 'Student',
    event_text: eventText,
    report_url: reportUrl,
  });
}
